import React, { useState, useEffect } from 'react';
import {jwtDecode} from 'jwt-decode';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Verificacion from '../../components/Verificacion';
import Suspendido from '../../components/Suspendido';
import AdminPrePanel from './admin/AdminPrePanel';
import MensajePanel from '../../warnings/MensajePanel';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CryptoJS from 'crypto-js';
import BASE_URL from '../../config/config';
import FotoPerfil from '../../components/ui/FotoPerfil';
import Loader from '../../components/Loader';
import NuevasFeatures from '../../warnings/NuevasFeatures';

const Dashboard = () => {
  const [showPass, setShowPass] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [minimoTiempoCumplido, setMinimoTiempoCumplido] = useState(false); // ⬅️ nuevo
  const [cantidadTickets, setCantidadTickets] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [activeTab, setActiveTab] = useState('historial');
  const [currentPage, setCurrentPage] = useState(1);
  const [ticketsPerPage, setTicketsPerPage] = useState(5);
  const [searchText, setSearchText] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [dashboardContent, setDashboardContent] = useState('dashboard');
  const [usuarioInfo, setUsuarioInfo] = useState(null);
  const token = localStorage.getItem('AuthToken');

  const tokenInfo = token ? jwtDecode(token) : {};
  const navigate = useNavigate();

  // Perfil states
  const [nombre, setNombre] = useState(tokenInfo.nombre || '');
  const [apellidos, setApellidos] = useState(tokenInfo.apellidos || '');
  const [email, setEmail] = useState(tokenInfo.email || '');
  const [telefono, setTelefono] = useState(tokenInfo.telefono || '');
  const [contrasena, setContrasena] = useState('');
  const [webs_del_cliente, setWebs_del_cliente] = useState(tokenInfo.webs_del_cliente || '');

  const encryptPassword = password => CryptoJS.MD5(password).toString();

  const handleLogout = () => {
    localStorage.removeItem('AuthToken');
    navigate('/iniciar-sesion', { replace: true });
    window.location.reload();
    window.scrollTo({ top: 0, behavior: 'smooth' });

  };

  const handleLinkClick = () => window.scrollTo({ top: 0 });

  const handleEditProfile = () => setDashboardContent('editProfile');
  const handleGoBack = () => setDashboardContent('dashboard');

  // Loader mínimo de 1s (como en el resto)
  useEffect(() => {
    const t = setTimeout(() => setMinimoTiempoCumplido(true), 1000);
    return () => clearTimeout(t);
  }, []);

  // Fetch cantidad de tickets
  useEffect(() => {
    const fetchCantidadTickets = async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/tickets/cantidad/${tokenInfo.usuario}`);
        if (resp.status === 200) setCantidadTickets(resp.data.cantidad);
      } catch (err) {
        console.error('Error al obtener la cantidad de tickets:', err);
      }
    };
    if (tokenInfo.usuario) fetchCantidadTickets();
  }, [tokenInfo.usuario]);

  // Fetch tickets
  useEffect(() => {
    const fetchTickets = async () => {
      setIsLoading(true);
      try {
        const resp = await axios.get(
          `${BASE_URL}/tickets/total/${tokenInfo.usuario}?page=${currentPage}&limit=${ticketsPerPage}`
        );
        if (resp.status === 200) {
          const sorted = resp.data.tickets.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setTickets(sorted);
          filterTickets(sorted);
        }
      } catch (err) {
        console.error('Error al obtener los tickets:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (activeTab === 'historial' && tokenInfo.usuario) fetchTickets();
  }, [activeTab, currentPage, ticketsPerPage, tokenInfo.usuario]);

  // Filtrado
  const filterTickets = list => {
    const filtered = list.filter(
      ticket =>
        (ticket.asunto.toLowerCase().includes(searchText.toLowerCase()) ||
          ticket.identificador.toString().includes(searchText)) &&
        (selectedDept === '' || ticket.dpto.toLowerCase() === selectedDept.toLowerCase())
    );
    setFilteredTickets(filtered);
  };

  useEffect(() => filterTickets(tickets), [searchText, tickets, selectedDept]);

  // Paginación
  const totalPages = Math.ceil(filteredTickets.length / ticketsPerPage);
  const indexOfLast = currentPage * ticketsPerPage;
  const indexOfFirst = indexOfLast - ticketsPerPage;
  const currentTickets = filteredTickets.slice(indexOfFirst, indexOfLast);

  // Fetch usuario info
  useEffect(() => {
    const fetchUsuarioInfo = async () => {
      try {
        const resp = await axios.get(
          `${BASE_URL}/usuario/info/${tokenInfo.usuario}`,
          { headers: { Authorization: token } }
        );
        setUsuarioInfo(resp.data.usuario);
      } catch (err) {
        console.error('Error al obtener info de usuario:', err);
      }
    };
    if (tokenInfo.usuario) fetchUsuarioInfo();
  }, [tokenInfo.usuario, token]);

  // Loader fullscreen (antes de renderizar contenido)
  const debeMostrarLoader =
    !minimoTiempoCumplido ||
    (dashboardContent === 'dashboard' && tokenInfo.role !== 'admin' && isLoading);

  if (debeMostrarLoader) {
    return (
      <div className="loader-fullscreen">
        <Loader />
      </div>
    );
  }

  if (tokenInfo.estado === 'verificar') return <Verificacion />;
  if (tokenInfo.estado === 'suspendido') return <Suspendido />;
  
  const handleSaveProfile = async () => {
    let encryptedPassword = contrasena ? encryptPassword(contrasena) : '';
    const updatedData = {
      nombre,
      apellidos,
      email,
      telefono,
      ...(contrasena && { contrasena: encryptedPassword }),
      webs_del_cliente,
    };
    try {
      const response = await axios.put(
        `${BASE_URL}/usuario/update/${tokenInfo.usuario}`,
        updatedData,
        { headers: { Authorization: token } }
      );
      if (response.status === 200) {
        toast.success('Perfil actualizado con éxito.');
        handleLogout();
      } else {
        toast.error('Hubo un problema al actualizar el perfil.');
      }
    } catch (error) {
      console.error('Error al actualizar el perfil:', error);
      toast.error('Ocurrió un error al actualizar el perfil.');
    }
  };

  const renderContent = () => {
    if (dashboardContent === 'editProfile') {
      return (
        <div className="edit-profile-section">
          <h2>Edita tu perfil</h2>
          <div className="grid-campos-ajustes">
            <div className="campo-ajuste">
              <label htmlFor="usuario">Usuario</label>
              <p>El nombre de usuario sirve para identificar todas tus acciones en la plataforma. ¿Problemas con tu usuario? Contáctanos directamente por teléfono al <a href="tel:+34 856 901 276">856 901 276</a> marcando la extensión #2 (Desarrollo).</p>
              <input type="text" name="usuario" id="usuario" value={`@${tokenInfo.usuario}`} disabled />
            </div>
            <hr />
            <div className="texto-edit-profile">
              <h3>Información Personal</h3>
              <p>Esta información sirve para dirigirnos hacia tí y contactar contigo en caso necesario de manera correcta.</p>
            </div>
            <div className="campo-ajuste">
              <label htmlFor="nombre">Nombre</label>
              <input type="text" id="nombre" value={nombre} onChange={e => setNombre(e.target.value)} />
            </div>
            <div className="campo-ajuste">
              <label htmlFor="apellidos">Apellidos</label>
              <input type="text" id="apellidos" value={apellidos} onChange={e => setApellidos(e.target.value)} />
            </div>
            <div className="campo-ajuste">
              <label htmlFor="email">Email</label>
              <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="campo-ajuste">
              <label htmlFor="telefono">Teléfono</label>
              <input type="text" id="telefono" value={telefono} onChange={e => setTelefono(e.target.value)} />
            </div>
            <div className="campo-ajuste">
              <label htmlFor="webs_del_cliente">Sitios en mantenimiento</label>
              <p>En caso de ser más de uno, separe sus dominios con comas.</p>
              <input type="text" id="webs_del_cliente" value={webs_del_cliente} onChange={e => setWebs_del_cliente(e.target.value)} />
            </div>
            <hr />
            <div className="texto-edit-profile">
              <h3>Seguridad</h3>
              <p>Modifica el acceso a tu cuenta.</p>
            </div>
            <div className="campo-ajuste">
              <label htmlFor="contrasena">Contraseña</label>

              <div className="input-with-toggle">
                <input
                  type={showPass ? 'text' : 'password'}
                  id="contrasena"
                  value={contrasena}
                  onChange={e => setContrasena(e.target.value)}
                  autoComplete="new-password"
                />

                <button
                  type="button"
                  className="toggle-visibility"
                  onClick={() => setShowPass(v => !v)}
                  aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  aria-pressed={showPass}
                  title={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPass ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
            </div>

            <div className="adhoc-warning">
              <p>AVISO: Al guardar unos nuevos ajustes, la sesión se cerrará. (Si el campo de contraseña está vacío no se guardará).</p>
            </div>
            <button onClick={handleSaveProfile}>Guardar perfil</button>
          </div>
          <ToastContainer />
        </div>
      );
    }

    return (
      <>
        <NuevasFeatures/>
        <MensajePanel />
        {tokenInfo.role === 'admin' ? (
          <AdminPrePanel />
        ) : (
          <div className="tabcontent">
            <div className='grid-historial'>
              <div className="texto-intro-dashboard">
                <h1>¡Hola, {tokenInfo.nombre}!</h1>
                <p>Nos alegramos de verte de nuevo por aquí. Consulta tus tickets y habla con un técnico en tiempo real.</p>
              </div>
              <div className="filtro-tickets">
                <input type="text" placeholder="Búsqueda por identificador o asunto..." value={searchText} onChange={e => setSearchText(e.target.value)} />
                <select value={selectedDept} onChange={e => setSelectedDept(e.target.value)}>
                  <option value="">Todos los departamentos</option>
                  <option value="no_especificado">Sin Especificar</option>
                  <option value="diseno">Diseño</option>
                  <option value="marketing">Marketing</option>
                  <option value="desarrollo">Desarrollo</option>
                  <option value="redes_sociales">Redes Sociales</option>
                </select>
              </div>

              {/* ⚠️ Quitamos el loader inline: el fullscreen ya cubre esta fase */}
              <div className="overflow-tabla">
                <div className='panel-tickets'>
                  <div className="ticket-list-header repeat-cliente">
                    <p>Identificador</p>
                    <p>Asunto</p>
                    <p>Estado</p>
                    <p>Departamento</p>
                    <p>Prioridad</p>
                    <p>Fecha</p>
                    <p>Asignado a</p>
                  </div>
                  <div className="tickets-list">
                    {filteredTickets.length === 0 ? (
                      <div className="no-tickets">¡No tienes tickets creados todavía!</div>
                    ) : (
                      currentTickets.map(ticket => (
                        <Link
                          key={ticket.identificador} // clave estable
                          className='link-ticket'
                          to={`/ticket/${ticket.identificador}`}
                          onClick={handleLinkClick}
                        >
                          <div className="ticket-item repeat-cliente">
                            <div className="ticket-field id">#{ticket.identificador}</div>
                            <div className="ticket-field asunto">
                              {ticket.asunto.split(' ').slice(0,5).join(' ')}
                              {ticket.asunto.split(' ').length > 5 ? '...' : ''}
                            </div>
                            <div className="ticket-field estado">
                              {ticket.estado_del_ticket === 'abierto' && <p className='abierto'>Abierto</p>}
                              {ticket.estado_del_ticket === 'cerrado' && <p className='cerrado'>Cerrado</p>}
                              {ticket.estado_del_ticket === 'retenido' && <p className='retenido'>Retenido</p>}
                            </div>
                            <div className="ticket-field dpto">
                              {ticket.dpto === 'no_especificado' && <p className='no-especificado'>Sin especificar</p>}
                              {ticket.dpto === 'desarrollo' && <p className='desarrollo'>Desarrollo</p>}
                              {ticket.dpto === 'diseno' && <p className='diseno'>Diseño</p>}
                              {ticket.dpto === 'marketing' && <p className='marketing'>Marketing</p>}
                              {ticket.dpto === 'redes_sociales' && <p className='redes-sociales'>Redes Sociales</p>}
                            </div>
                            <div className="ticket-field prioridad">
                              {ticket.prioridad === 'no_urgente' && <p className='sin-prioridad'>No urgente</p>}
                              {ticket.prioridad === 'urgente' && <p className='alta'>Urgente</p>}
                            </div>
                            <div className="ticket-field fecha">{new Date(ticket.createdAt).toLocaleDateString()}</div>
                            <div className="ticket-field asignado">
                              {ticket.usuario_encargado === 'sin_asignar' ? (
                                <div className='asignado'><img src="/images/sin_asignar.svg" alt="Sin asignar" /><p>Sin asignar</p></div>
                              ) : (
                                <div className='asignado'><img src="/images/asignado.svg" alt={ticket.usuario_encargado} /><p>@{ticket.usuario_encargado}</p></div>
                              )}
                            </div>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                  {filteredTickets.length > ticketsPerPage && (
                    <div className="pagination paginacion-cliente">
                      <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>Anterior</button>
                      <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>Siguiente</button>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <main className='site-width'>
      <div className="dashboard-grid">
        <div className='profile-and-menu'>
          <div className='profile'>
            <FotoPerfil />
            {tokenInfo.nombre && tokenInfo.apellidos && (
              <p className='profile-name'>{tokenInfo.nombre} {tokenInfo.apellidos}<br /><span>@{tokenInfo.usuario}</span></p>
            )}
            {usuarioInfo && usuarioInfo.role === 'admin' && (
              <div className='admin-info'>
                <p className="admin-chapa">Administrador</p>
                <p>
                  Departamento:  
                  {usuarioInfo.dpto_tecnico === 'desarrollo' ? ' Desarrollo' : 
                  usuarioInfo.dpto_tecnico === 'marketing' ? ' Marketing' : 
                  usuarioInfo.dpto_tecnico === 'diseno' ? ' Diseño' : 
                  usuarioInfo.dpto_tecnico === 'redes_sociales' ? ' Redes Sociales' : 
                  ' Todos'
                  }
                </p>
              </div>
            )}
            <div className="profile-actions">
              <button className='edit-profile' onClick={handleEditProfile}>Editar perfil</button>
              <button className='logout' onClick={handleLogout}><img src="/images/cerrar-sesion.svg" alt="Logout" /><p>Salir</p></button>
            </div>
          </div>

          <div className="dashboard-stats">
            <div className="bloque-secciones">
              <div className="bloque-seccion" onClick={handleGoBack}><img src="/images/ticket-lord.svg" alt="Tickets" />{tokenInfo.role === 'admin' ? <p>Administración</p> : <p>Mis tickets</p>}</div>
              {tokenInfo.role !== 'admin' && <Link to="/dashboard/proyectos"><div className="bloque-seccion"><img src="/images/website.svg" alt="Proyectos" /><p>Mis proyectos</p></div></Link>}
              <Link to="/dashboard/servicios" onClick={handleLinkClick}><div className="bloque-seccion"><img src="/images/contrato-lord.svg" alt="Servicios" /><p>Servicios contratados</p></div></Link>
              <Link to="https://agenciaadhoc.com/reportes" target="_blank"><div className="bloque-seccion"><img src="/images/star-lord.svg" alt="Vota mejoras" /><p>Reporta errores o sugiere mejoras</p></div></Link>
            </div>
            {
              tokenInfo.role !== 'admin' && (
                cantidadTickets !== null && <div className="bloque-stats"><img src="/images/ticket-lord.svg" alt="Trofeo" /><p>Tickets creados: <span>{cantidadTickets}</span></p></div>
              )
            }
          </div>
        </div>
        <div className="dashboard-content">
          {renderContent()}
        </div>
      </div>
    </main>
  );
};

export default Dashboard;
